import { useEffect, useMemo, useState, useCallback } from "react";
import { Download, LogOut, Search, Trash2, RefreshCw, Check, Users } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../Logo";
import { adminApi } from "../../lib/api";
import { ClassDateCard } from "./ClassDateCard";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("ms-MY", { dateStyle: "medium", timeStyle: "short" }) : "-");

const statusLabel = (r) => (r.joined_telegram ? "Sudah Join Telegram" : r.contacted ? "Sudah Dihubungi" : "Perlu Follow-up");

const toCsv = (rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["Nama", "Number Fon", "Email", "Telegram", "Tarikh", "Status", "Dirujuk Oleh", "Jumlah Rujukan"];
  const body = rows.map((r) => [r.nama, r.phone, r.email, r.telegram, fmt(r.created_at), statusLabel(r), r.referred_by_name || "", r.referral_count ?? 0].map(esc).join(","));
  return [head.join(","), ...body].join("\n");
};

const Stat = ({ label, value, testid }) => (
  <div className="glass rounded-2xl p-5" data-testid={testid}>
    <div className="font-mono text-xs tracking-widest text-violet-200">{label}</div>
    <div className="font-display text-4xl font-black mt-2">{value}</div>
  </div>
);

const RefBadge = ({ r }) =>
  (r.referral_count ?? 0) > 0 ? (
    <span data-testid={`referral-count-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold bg-fuchsia-500/15 border border-fuchsia-400/40 text-pink-200">
      <Users className="w-3.5 h-3.5" /> {r.referral_count}
    </span>
  ) : (
    <span className="text-xs text-violet-200/70">0 rujukan</span>
  );

const Via = ({ r }) =>
  r.referred_by_name ? (
    <div className="text-xs text-pink-200 mt-0.5" data-testid={`referred-by-${r.id}`}>via {r.referred_by_name}</div>
  ) : null;

const TopReferrers = ({ rows }) => {
  const top = rows.filter((r) => (r.referral_count ?? 0) > 0).sort((a, b) => b.referral_count - a.referral_count).slice(0, 5);
  if (!top.length) return null;
  return (
    <div className="glass rounded-2xl p-5 mt-4 max-w-xl" data-testid="top-referrers">
      <div className="font-mono text-xs tracking-widest text-violet-200 mb-3">TOP PERUJUK</div>
      <ol className="space-y-2">
        {top.map((r, i) => (
          <li key={r.id} className="flex items-center justify-between gap-3 text-sm">
            <span className="truncate text-white"><span className="font-mono text-violet-300 mr-3">0{i + 1}</span>{r.nama}</span>
            <span className="font-display font-bold text-white whitespace-nowrap">{r.referral_count} <span className="text-xs font-sans font-normal text-violet-200">rujukan</span></span>
          </li>
        ))}
      </ol>
    </div>
  );
};

const FILTERS = [
  { k: "all", l: "Semua" },
  { k: "followup", l: "Perlu Follow-up" },
  { k: "joined", l: "Sudah Join Telegram" },
  { k: "contacted", l: "Sudah Dihubungi" },
];

const matchFilter = (r, f) =>
  f === "all" ||
  (f === "joined" && r.joined_telegram) ||
  (f === "contacted" && !r.joined_telegram && r.contacted) ||
  (f === "followup" && !r.joined_telegram && !r.contacted);

const JoinedBadge = ({ r }) => (
  <span
    data-testid={`status-joined-${r.id}`}
    title={r.joined_at ? `Join: ${fmt(r.joined_at)}` : ""}
    className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap bg-emerald-500/15 border border-emerald-400/40 text-emerald-200"
  >
    <span className="w-4 h-4 rounded-full grid place-items-center bg-emerald-400 text-[#0B0518]"><Check className="w-3 h-3" strokeWidth={3} /></span>
    Sudah Join Telegram
  </span>
);

const StatusToggle = ({ r, onToggle }) => r.joined_telegram ? <JoinedBadge r={r} /> : (
  <button
    onClick={() => onToggle(r)}
    data-testid={`toggle-contacted-${r.id}`}
    title={r.contacted_at ? `Dihubungi: ${fmt(r.contacted_at)}` : "Tandakan sudah dihubungi"}
    className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap border transition-colors duration-300 ${
      r.contacted
        ? "bg-sky-500/15 border-sky-400/40 text-sky-200 hover:bg-sky-500/25"
        : "bg-amber-500/10 border-amber-400/50 text-amber-200 hover:bg-amber-500/20"
    }`}
  >
    <span className={`w-4 h-4 rounded-full grid place-items-center ${r.contacted ? "bg-sky-400 text-[#0B0518]" : "border border-amber-300/70"}`}>
      {r.contacted && <Check className="w-3 h-3" strokeWidth={3} />}
    </span>
    {r.contacted ? "Sudah Dihubungi" : "Perlu Follow-up"}
  </button>
);

export const AdminDashboard = ({ onLogout }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/bookings");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
      else toast.error("Gagal memuatkan data");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return rows.filter(
      (r) =>
        matchFilter(r, filter) &&
        [r.nama, r.phone, r.email, r.telegram].some((v) => String(v ?? "").toLowerCase().includes(s))
    );
  }, [rows, q, filter]);

  const pending = rows.filter((r) => !r.joined_telegram && !r.contacted).length;

  const toggle = async (r) => {
    try {
      const { data } = await adminApi().patch(`/admin/bookings/${r.id}`, { contacted: !r.contacted });
      setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, ...data } : x)));
      toast.success(data?.contacted ? "Ditanda sudah dihubungi" : "Ditanda belum dihubungi");
    } catch {
      toast.error("Gagal mengemas kini status");
    }
  };

  const today = rows.filter((r) => new Date(r.created_at).toDateString() === new Date().toDateString()).length;

  const exportCsv = () => {
    const blob = new Blob(["\ufeff" + toCsv(filtered)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `booking-rahsia-trading-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const remove = async (id) => {
    if (!window.confirm("Padam booking ini?")) return;
    try {
      await adminApi().delete(`/admin/bookings/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success("Booking dipadam");
    } catch {
      toast.error("Gagal memadam");
    }
  };

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-[#0B0518]/80 backdrop-blur-xl border-b border-violet-500/15">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <button onClick={onLogout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 text-sm text-violet-200 hover:text-white transition-colors">
            <LogOut className="w-4 h-4" /> Log Keluar
          </button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display uppercase font-black text-3xl sm:text-4xl">Senarai Booking</h1>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-8 max-w-3xl">
          <Stat label="JUMLAH" value={rows.length} testid="stat-total" />
          <Stat label="HARI INI" value={today} testid="stat-today" />
          <Stat label="FOLLOW-UP" value={pending} testid="stat-pending" />
          <Stat label="VIA RUJUKAN" value={rows.filter((r) => r.referred_by).length} testid="stat-referred" />
        </div>
        <TopReferrers rows={rows} />
        <ClassDateCard />
        <div className="flex gap-2 mt-6 overflow-x-auto pb-1" data-testid="status-filter">
          {FILTERS.map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k}`}
              className={`rounded-full px-4 py-2 text-sm whitespace-nowrap border transition-colors duration-300 ${
                filter === f.k ? "bg-violet-500 border-violet-400 text-white" : "border-violet-400/30 text-violet-200 hover:bg-violet-500/15"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <div className="flex-1 flex items-center gap-3 glass rounded-full px-5">
            <Search className="w-4 h-4 text-violet-300" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama, fon, email, telegram..." data-testid="admin-search-input" className="flex-1 bg-transparent outline-none py-3 text-sm text-white placeholder:text-violet-200/50" />
          </div>
          <button onClick={load} data-testid="admin-refresh-btn" className="glass rounded-full px-5 py-3 text-sm inline-flex items-center justify-center gap-2 hover:bg-violet-500/20 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Muat Semula
          </button>
          <button onClick={exportCsv} disabled={!filtered.length} data-testid="admin-export-csv-btn" className="btn-neon rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
        <div className="md:hidden mt-6 space-y-3" data-testid="bookings-cards">
          {filtered.map((r) => (
            <div key={r.id} className="glass rounded-2xl p-5" data-testid={`booking-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-white font-semibold truncate">{r.nama}</div>
                  <div className="text-xs text-violet-200/75 mt-0.5">{fmt(r.created_at)}</div>
                  <Via r={r} />
                </div>
                <button onClick={() => remove(r.id)} data-testid={`delete-booking-m-${r.id}`} className="text-violet-200/70 hover:text-pink-300 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="mt-3 space-y-1 text-sm break-all">
                <a href={`https://wa.me/${String(r.phone).replace(/\D/g, "").replace(/^0/, "60")}`} target="_blank" rel="noopener noreferrer" className="block text-violet-100">{r.phone}</a>
                <div className="text-violet-100">{r.email}</div>
                <a href={`https://t.me/${String(r.telegram).replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="block text-violet-300">{r.telegram}</a>
              </div>
              <div className="mt-4 flex items-center justify-between gap-3"><StatusToggle r={r} onToggle={toggle} /><RefBadge r={r} /></div>
            </div>
          ))}
          {!filtered.length && !loading && <p className="text-center text-violet-200/70 py-10">Tiada booking.</p>}
        </div>
        <div className="hidden md:block glass rounded-3xl mt-6 overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]" data-testid="bookings-table">
            <thead>
              <tr className="text-left font-mono text-xs tracking-widest text-violet-200 border-b border-violet-500/20">
                {["STATUS", "NAMA", "NUMBER FON", "EMAIL", "TELEGRAM", "RUJUKAN", "TARIKH", ""].map((h) => <th key={h} className="px-5 py-4 font-normal">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-violet-500/10 hover:bg-violet-500/5 transition-colors" data-testid={`booking-row-${r.id}`}>
                  <td className="px-5 py-4"><StatusToggle r={r} onToggle={toggle} /></td>
                  <td className="px-5 py-4 text-white">{r.nama}<Via r={r} /></td>
                  <td className="px-5 py-4"><a href={`https://wa.me/${String(r.phone).replace(/\D/g, "").replace(/^0/, "60")}`} target="_blank" rel="noopener noreferrer" className="hover:text-violet-300">{r.phone}</a></td>
                  <td className="px-5 py-4">{r.email}</td>
                  <td className="px-5 py-4"><a href={`https://t.me/${String(r.telegram).replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:text-white">{r.telegram}</a></td>
                  <td className="px-5 py-4"><RefBadge r={r} /></td>
                  <td className="px-5 py-4 text-violet-100/75">{fmt(r.created_at)}</td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => remove(r.id)} data-testid={`delete-booking-${r.id}`} className="text-violet-200/70 hover:text-pink-300 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {!filtered.length && !loading && (
                <tr><td colSpan={8} className="px-5 py-12 text-center text-violet-200/70" data-testid="bookings-empty">Tiada booking.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};
